import { StateContext, useEvaluableControls } from "@client/pages/round/Round";
import { NodeColors } from "@client/utils/NodeColors";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import {
  dollarsFromMVF,
  ldtFromMVF,
  valueFromMVF,
} from "@lib/tranche/trancheUtils";
import * as d3 from "d3";
import React, { useContext } from "react";
import { clipLineToBox } from "../../../../utils/clipLineToBox";
import { FrontendEvaluation, MVFUpdate } from "../useEvaluatorEvaluationsCache";
import { D3Axis } from "./MVFGraph";
import {
  HoldControls,
  PiecewiseControls,
  piecewiseLineClickHandler,
} from "./PiecewiseControls";

function FDVLabel({
  evaluation,
  color,
  firstPoint,
  xAxis,
  yAxis,
}: {
  evaluation: FrontendEvaluation;
  color: string;
  firstPoint: [number, number];
  xAxis: D3Axis;
  yAxis: D3Axis;
}) {
  function openEvaluablePage() {}

  const name = evaluation.evaluable.shortname;

  const elementRef = (element: Element | null) => {
    if (!element) {
      return;
    }

    const selection = d3.select(element);
    selection.on("dblclick", (_event) => openEvaluablePage());
  };

  const isAboveGraph = firstPoint[1] >= yAxis.domain()[1];

  let truncatedName: string;
  let x = xAxis(firstPoint[0]);
  let y = yAxis(firstPoint[1]);
  if (isAboveGraph) {
    y -= 6;
    x += 12;
    truncatedName = name;
  } else {
    truncatedName = name.length > 15 ? name.slice(0, 15) + "..." : name;
    x -= 12;
    y += 4;
  }

  return (
    <>
      {!isAboveGraph && (
        <rect
          className="fill-white dark:fill-[#181818]"
          x={x - 52}
          y={y - 12}
          width={58}
          height={15}
        ></rect>
      )}
      <text
        className="cursor-pointer"
        ref={elementRef}
        textAnchor="end"
        fill={color}
        strokeWidth={0}
        style={{ fontSize: 12 }}
        x={x}
        y={y}
      >
        {truncatedName}
      </text>
    </>
  );
}

export function MVFFunction({
  evaluation,
  mvfUpdate,
  xAxis,
  yAxis,
  fillToY,
  isEditable,
  isHold,
}: {
  evaluation: FrontendEvaluation;
  mvfUpdate: MVFUpdate;
  xAxis: D3Axis;
  yAxis: D3Axis;
  fillToY: number;
  isEditable: boolean;
  isHold: boolean;
}) {
  const { round } = useContext(StateContext);
  const evaluableControls = useEvaluableControls(evaluation.evaluableId);
  const { isDarkMode } = useTheme();

  const { mvf, evaluable } = evaluation;

  const [xMin, xMax] = xAxis.domain();
  const [yMin, yMax] = yAxis.domain();
  const strokeWidth = evaluableControls.isFocus ? 2.7 : 1.8;

  const initialColor = NodeColors.getColor(
    round,
    evaluable,
    !evaluableControls.isFocus,
  );

  const color = isDarkMode
    ? initialColor
    : new TinyColor(initialColor).darken(10).toHslString();

  function attachEventListeners(element: Element | null) {
    if (!element || !isEditable) {
      return;
    }

    const selection = d3.select(element);

    selection.on("dblclick", (e) => {
      piecewiseLineClickHandler(e, evaluation, mvfUpdate, xAxis, yAxis);
    });
  }

  function mouseOverHandler(event: React.MouseEvent<SVGPathElement>) {
    // 1 = left click (so we skip handling if the left button is pressed)
    if ((event.buttons & 1) === 1) {
      return;
    }

    evaluableControls.hover();
  }

  const pointsForLine: [number, number][] = [];
  for (let i = 0; i < mvf.length - 1; i++) {
    const point = mvf[i];
    const nextPoint = mvf[i + 1];

    const res = clipLineToBox(
      point.dollar,
      point.value,
      nextPoint.dollar,
      nextPoint.value,
      xMin,
      yMin,
      xMax,
      yMax,
    );

    if (!res) {
      continue;
    }

    const { x1, y1, x2, y2 } = res;
    if (pointsForLine.length === 0) {
      pointsForLine.push([x1, y1]);
    }

    pointsForLine.push([x2, y2]);
  }

  if (pointsForLine.length === 0) {
    return null;
  }

  const lineGenerator = d3
    .line()
    .x((d) => xAxis(d[0]))
    .y((d) => yAxis(d[1]));

  const line = lineGenerator(pointsForLine);
  if (!line) {
    console.error("Failed to generate line");
    return null;
  }

  let area: string | null = null;
  if (fillToY < yMax) {
    const pointsForArea: [number, number][] = [];
    if (pointsForLine[0][1] >= yMax) {
      pointsForArea.push([xMin, yMax]);
    }

    for (const [x, y] of pointsForLine) {
      if (y > fillToY) {
        pointsForArea.push([x, y]);
        continue;
      }

      const dollars = dollarsFromMVF(mvf, fillToY);
      pointsForArea.push([dollars, fillToY]);

      break;
    }

    const areaGenerator = d3
      .area()
      .x((d) => xAxis(d[0]))
      .y0(yAxis(fillToY))
      .y1((d) => yAxis(d[1]));

    area = areaGenerator(pointsForArea);
    if (!area) {
      console.error("Failed to generate area");
    }
  }

  return (
    <g key={`${evaluation.evaluableId}-${evaluation.evaluatorId}`}>
      <path
        className="line"
        ref={attachEventListeners}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        d={line}
        onMouseOver={mouseOverHandler}
        // Handling mouseout leads to flicker (https://survivalandflourishin.slack.com/archives/C07R431BW4X/p1739314490037819).
        // onMouseOut={evaluableControls.unhover}
        strokeDasharray={evaluation.isDeferred ? "4" : ""}
      >
        <title>{evaluable.shortname}</title>
      </path>
      {area && (
        <path
          className="pointer-events-none opacity-70"
          fill={new TinyColor(color)
            .setAlpha((1 - evaluation.fdv / 3) / 4)
            .toHslString()}
          d={area}
        />
      )}
      {ldtFromMVF(mvf) > xMax && (
        <text
          x={xAxis(xMax) + 15}
          y={yAxis(valueFromMVF(mvf, xMax)) + 5}
          fill={color}
        >
          {isHold ? "Hold" : d3.format("$.2s")(ldtFromMVF(mvf))}
        </text>
      )}

      {evaluableControls.isFocus && (
        <FDVLabel
          firstPoint={pointsForLine[0]}
          xAxis={xAxis}
          yAxis={yAxis}
          color={color}
          evaluation={evaluation}
        />
      )}

      {isEditable &&
        evaluableControls.isFocus &&
        (isHold ? (
          <HoldControls
            evaluation={evaluation}
            xAxis={xAxis}
            yAxis={yAxis}
            mvfUpdate={mvfUpdate}
            color={color}
          />
        ) : (
          <PiecewiseControls
            evaluation={evaluation}
            xAxis={xAxis}
            yAxis={yAxis}
            mvfUpdate={mvfUpdate}
            color={color}
          />
        ))}
    </g>
  );
}
